import { inject, computed } from '@angular/core';
import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap, catchError, of, filter, take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { TPersonModule, SystemModules } from './entities/system-modules.model';
import { SystemModulesService } from './infra/system-modules.service';
import { tapResponse } from '@ngrx/operators';

export type TSystemModulesStore = {
  modules: TPersonModule[];
  allowedModuleIds: Set<string>;
  isConnectionError: boolean;
};

export const initialSystemModulesStore: TSystemModulesStore = {
  modules: [],
  allowedModuleIds: new Set<string>(),
  isConnectionError: false,
};

export const SystemModulesStore = signalStore(
  { providedIn: 'root' },
  withState<TSystemModulesStore>(initialSystemModulesStore),
  withReqState('loadPersonModules'),
  withComputed((store) => ({
    activeModules: computed(() => 
      store.modules().filter((module) => module.IsActive && module.IsValid)
    ),
    
    hasActiveModules: computed(() => 
      store.modules().some((module) => module.IsActive && module.IsValid)
    ),
    
    moduleNames: computed(() => {
      const MODULE_NAME_MAPPING: Record<string, string> = {
        '45c45478-0efc-4def-8ff9-7636e3e72c18': 'Actuar',
        '24a44ab0-4d95-4e98-9eb5-587553c5d7e4': 'Acesso',
        '3085ce66-a968-49ee-a88e-b5b9c7e2e59a': 'AvaliacaoFisica',
        '38299a3b-5e4c-447a-ba9a-ab9e7f19b4d2': 'Treino',
        'b733f377-b7b1-4701-b654-62ee6d835137': 'Cobranca',
        '475658a0-236b-4772-b77b-e771ecca66d2': 'Gestor',
        'aecd8154-509a-4eec-9533-0ef14b975e35': 'Helpdesk',
        'f45c6900-64d6-4e04-a273-46a410501532': 'ActuarWeb',
        '6e2a0f78-a58f-47be-8f91-85babcbe5b9f': 'Crm',
        '033ac824-6786-4071-9180-e9c10d742ef1': 'Facial',
        '6be95655-afba-492d-90d4-4637397b3b84': 'Contratos',
        '78567d75-39c1-43df-9ece-cc32ff554de2': 'Gympass',
        '2322c49a-0ba8-4056-9854-3892edfe3f16': 'NotaFiscal',
        '7d306ae7-3a62-4d2e-bbb7-ec973f029a84': 'Totalpass',
        'd8fda503-805e-4746-8003-f2c050b03321': 'Whatsapp'
      };
      
      return store.modules()
        .filter((module) => module.IsActive && module.IsValid)
        .map((module) => MODULE_NAME_MAPPING[module.ProductId])
        .filter(Boolean);
    })
  })),
  withMethods(
    (
      store,
      systemModulesService = inject(SystemModulesService),
      errorStore = inject(ErrorStore)
    ) => ({
      loadPersonModules: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadPersonModules'), { isConnectionError: false })),
          switchMap(() => {
            return systemModulesService.getPersonModules().pipe(
              tapResponse({
                next: (modules: TPersonModule[]) => {
                  const allowedModuleIds = new Set<string>(
                    modules
                      .filter((module) => module.IsActive && module.IsValid)
                      .map((module) => module.ProductId)
                  );
                  patchState(store, {
                    modules,
                    allowedModuleIds,
                    isConnectionError: false,
                  });
                  patchState(store, setPropLoaded('loadPersonModules'));
                },
                error: (error: unknown) => {
                  const isConnectionError = error instanceof HttpErrorResponse && 
                    (error.status === 0 || !navigator.onLine);
                  
                  patchState(store,{ isConnectionError }, setPropError('loadPersonModules', error));
                  
                  if (!isConnectionError) {
                    if (error instanceof HttpErrorResponse) {
                      errorStore.setHttpError(error);
                    } else {
                      errorStore.setJavascriptError(error);
                    }
                  }
                },
              })
            );
          }),
          catchError((error) => {
            const isConnectionError = error instanceof HttpErrorResponse && 
              (error.status === 0 || !navigator.onLine);
            
            patchState(store,{ isConnectionError }, setPropError('loadPersonModules', error));
            
            if (!isConnectionError) {
              if (error instanceof HttpErrorResponse) {
                errorStore.setHttpError(error);
              } else {
                errorStore.setJavascriptError(error);
              }
            }
            return of([]);
          })
        )
      ),

      isModuleAllowed: (moduleId: SystemModules | string): boolean => {
        return store.allowedModuleIds().has(moduleId);
      },

      isSystemAllowed: (requiredModules: SystemModules[]): boolean => {
        const allowedIds = store.allowedModuleIds();
        return requiredModules.every((moduleId) => allowedIds.has(moduleId));
      },

      getAllowedModules: (): TPersonModule[] => {
        return store.activeModules();
      },
      
      hasAnyActiveModule: (): boolean => {
        return store.hasActiveModules();
      },

      refreshModules: rxMethod<void>(
        pipe(
          switchMap(() => {
            return systemModulesService.getPersonModules().pipe(
              tapResponse({
                next: (modules: TPersonModule[]) => {
                  const allowedModuleIds = new Set<string>(
                    modules
                      .filter((module) => module.IsActive && module.IsValid)
                      .map((module) => module.ProductId)
                  );
                  patchState(store, {
                    modules,
                    allowedModuleIds,
                  });
                },
                error: (error: unknown) => {
                  if (error instanceof HttpErrorResponse) {
                    errorStore.setHttpError(error);
                  } else {
                    errorStore.setJavascriptError(error);
                  }
                },
              })
            );
          })
        )
      ),
    })
  )
);

